import { useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import { FOR_PROTOCOL_NOTE } from '../../constants';

export type ArticleSaveAs = 'sop' | 'knowledge';

interface SaveArticleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSave: (saveAs: ArticleSaveAs) => void;
  isSaving?: boolean;
}

const OPTIONS: Array<{ value: ArticleSaveAs; label: string; description: string }> = [
  { value: 'sop', label: 'For a protocol', description: FOR_PROTOCOL_NOTE },
  { value: 'knowledge', label: 'Knowledge', description: 'Will be treated as static information' }
];

export function SaveArticleDialog({ open, onOpenChange, onSave, isSaving }: SaveArticleDialogProps) {
  const [saveAs, setSaveAs] = useState<ArticleSaveAs>('sop');

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='w-[calc(100vw-2rem)] max-w-md p-4'>
        <DialogHeader>
          <DialogTitle className='text-sm font-semibold'>Save As</DialogTitle>
        </DialogHeader>

        <RadioGroup value={saveAs} onValueChange={(v) => setSaveAs(v as ArticleSaveAs)} className='gap-0'>
          {OPTIONS.map((option, index) => (
            <Label
              key={option.value}
              htmlFor={`save-as-${option.value}`}
              className={cn('flex items-start gap-3 py-3 cursor-pointer', index > 0 && 'border-t')}
            >
              <RadioGroupItem value={option.value} id={`save-as-${option.value}`} className='mt-0.5' />
              <div>
                <p className='text-sm font-medium'>{option.label}</p>
                <p className='text-xs text-muted-foreground'>{option.description}</p>
              </div>
            </Label>
          ))}
        </RadioGroup>

        <div className='flex gap-2 justify-end mt-2'>
          <Button type='button' variant='outline' className='text-xs' onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button type='button' className='text-xs' onClick={() => onSave(saveAs)} disabled={isSaving}>
            {isSaving ? 'Saving…' : 'Save'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
